import React from "react";
import {
    Heading,
    Paragraph,
    UnorderedList,
    OrderedList,
    ListItem
} from "@piggybank/core";
import { useTranslation } from "react-i18next";
import { unorderedListType, remarkData, illustrativeData, kfsTableData } from "./CreditCardKFSData";
import { getUserSessionDataByKey } from 'storage/userStorage';

const View = ({ value }) => {
    const { t } = useTranslation();
    if (!value) {
        return null;
    }

    if (React.isValidElement(value)) {
        return value;
    }

    return <span dangerouslySetInnerHTML={{ __html: t(`ICO.CREDIT.${value}`) }} />
};

const specialCaseLogic = (row) => {
    // Special case logic for KFS_LATE_PAYMENT_FEE
    if (row.key === 'KFS_LATE_PAYMENT_FEE') {
        const { lang } = getUserSessionDataByKey('global');
        const firstItemType = row.value[0]?.type;

        if((lang === 'en' && firstItemType === 'p') || (lang !== 'en' && firstItemType !== 'p')){
            const firstValue = row.value.shift();
            row.value.push(firstValue);
        }
    }
    return row;
};

const TableCellValue = ({ row = {} }) => {
    if (!row.value || !row.value.length) {
        return null;
    }

    row = specialCaseLogic(row);
    return row.value?.map(value => {
        if (value.type === "p") {
            return (
                <Paragraph key={value.content} marginBottom={2}>
                    <View value={value.content} />
                </Paragraph>);
        } else if (value.type === "l") {
            return (
                <ListGenerator key={JSON.stringify(value?.items)} data={{ items: value.items, listType: value.listType }} />
            );
        } else if (value.type === "t") {
            return (<GenerateCommonTable key={JSON.stringify(value?.content)} data={value.content} />);
        }
        return null;
    });
};

// Generate OrderedList & UnorderedList
const ListGenerator = ({ data = {} }) => {
    const { items = [], listType } = data;
    if (!items.length) {
        return null;
    }

    let List = OrderedList;
    if (unorderedListType.includes(listType)) {
        List = UnorderedList;
    }

    return (
        <List marginBottom={2} type={listType} spacing={1}>
            {items.map(item => (
                <ListItem key={`${item}`}><View value={item} /></ListItem>
            ))}
        </List>
    );
};

const GenerateTableRow = ({ row }) => {
    if (row.title) {
        return (
            <tr>
                <td colSpan="2" className="row-first-level-title">
                    <View value={row.title} />
                </td>
            </tr>
        );
    }
    return (
        <tr>
            <td className="table-first-column">
                <View value={row.key} />
            </td>
            <td>
                <TableCellValue row={row} />
            </td>
        </tr>
    );
}

const GenerateKFSTable = ({ data = {} }) => (
    <div className="kfs-table kfs-table-margin-botton">
        <table>
            <colgroup>
                <col style={{ width: '34%' }} />
                <col style={{ width: '66%' }} />
            </colgroup>
            <thead>
                <tr>
                    <th colSpan="2">
                        <Paragraph marginBottom={1} style={{ textAlign: 'center' }}><View value={data.header?.[0]} /></Paragraph>
                        <Paragraph marginBottom={2}><View value={data.header?.[1]} /></Paragraph>
                        <Paragraph marginBottom={2}><View value={data.header?.[2]} /></Paragraph>
                    </th>
                </tr>
            </thead>
            <tbody>
                {data.body?.map(row => (
                    <GenerateTableRow key={row.key} row={row} />
                ))}
            </tbody>
        </table>
    </div>
);

const CommonTableBody = ({ data = {} }) => {
    if (!data.body || !data.body.length) {
        return null;
    }

    const headerLength = data.header?.length || 0;

    return data.body.map(row => (
        <tr key={JSON.stringify(row)}>
            {row.map((cell, index)=> {
                const isTtitle = headerLength === row.length;
                return (
                    <td colSpan={isTtitle ? 1 : headerLength}
                        className={isTtitle ? "" : "kfs-table-annual-fee-title"}
                        key={`${JSON.stringify(cell)}${index}`}>
                        <View value={typeof cell !== 'object' ? cell : <ListGenerator data={cell} />} />
                    </td>
                )
            })}
        </tr>
    ))
};

// Generate common table
const GenerateCommonTable = ({ data = {} }) => (
    <div className="kfs-common-table kfs-table">
        <table>
            <thead>
                <tr>
                    {data.header?.map(item => (
                        <th key={item}><View value={item} /></th>
                    ))}
                </tr>
            </thead>
            <tbody>
                <CommonTableBody data={data} />
            </tbody>
        </table>
    </div>
);

const CreditCardKFS = () => {
    const { t } = useTranslation();

    return (
        <>
            <Heading level={2} accentBar>{t('ICO.CREDIT.KFS')}</Heading>
            <Heading level={3}>{t('ICO.CREDIT.KFS_TITLE')}</Heading>

            <div className="kfs-sub-title-font">
                <Paragraph>{t('ICO.CREDIT.KFS_SUBTITLE')}</Paragraph>
                <div style={{ textAlign: 'right' }}>
                    <Paragraph>{t('ICO.CREDIT.KFS_CARD')}<br />{t('ICO.CREDIT.KFS_DATE')}</Paragraph>
                </div>
            </div>


            {/****************KFS Table*****************/}
            <GenerateKFSTable data={kfsTableData} />

            {/****************Illustrative*****************/}
            <Heading level={4}>{t('ICO.CREDIT.KFS_ILLUSTRATIVE_TABLE')}</Heading>
            <Paragraph marginBottom={0}>
                <span className="kfs-llustrative-underline">{t('ICO.CREDIT.KFS_ILLUSTRATIVE_TABLE_ASSUMPTIONS')}</span>
                <span>{t('ICO.CREDIT.KFS_ILLUSTRATIVE_TABLE_ASSUMPTIONS_STRIKETHROUGH_CHAR')}</span>
            </Paragraph>
            <ListGenerator data={illustrativeData.list} />
            <Paragraph marginBottom={2}>{t('ICO.CREDIT.KFS_ILLUSTRATIVE_DESC')}</Paragraph>
            <div className="kfs-table-margin-botton kfs-illustrative-session"><GenerateCommonTable data={illustrativeData.table} /></div>

            {/****************Remarks*****************/}
            <Heading level={4}>{t('ICO.CREDIT.KFS_REMARKS')}</Heading>
            <ListGenerator data={remarkData.list} />
            <Paragraph >{t('ICO.CREDIT.KFS_ENCOURAGE_APPLY_CREDIT_CARD')}</Paragraph>
            <Paragraph marginBottom={6}>{t('ICO.CREDIT.KFS_LANGUAGE_VERSION_DIFF_CLARIFY')}</Paragraph>
        </>
    )
}

export default CreditCardKFS;
